import {
    select as D3select,
    dispatch as D3dispatch,
    scaleOrdinal as D3scaleOrdinal,
    scaleSqrt as D3scaleSqrt,
    arc as D3arc,
    pie as D3pie,
    rollup as D3rollup,
    max as D3max,
    sum as D3sum,
    format as D3format
} from 'd3';
import { debounce } from 'lodash';

import D3Tooltips from './D3Tooltips.js';

// a d3 donut chart component
// expects data:
// [{
//     count: 10,
//     key: 'src_ip = 1.2.3.4, src_port = 123',
//     groups: [{key: 'src_ip', value: '1.2.3.4'}, {key: 'src_port', value: 123}]
// }]
const D3ProgDonut = function () {
    const dispatch = D3dispatch('click', 'mouseover');

    // default settings can be overridden
    let progdata;
    let size = 132;
    let colors = D3scaleOrdinal();
    let progColors = D3scaleOrdinal();
    let date; // for display only
    let group;
    let theme;

    // scales
    const radius = D3scaleSqrt().range([0, 220]);
    const pie = D3pie().value(d => d.value).sort((a, b) => a.key - b.key);
    const arc = D3arc();
    const arc2 = D3arc();
    const keyArc = D3arc();
    const margin = { top: 4, right: 10, bottom: 4, left: 10 };

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            const cheight = size - margin.top - margin.bottom;
            const pieRadius = cheight / 2;
            const pieOutRadius = pieRadius;
            const pieInRadius = pieRadius - 20;
            arc.outerRadius(pieOutRadius).innerRadius(pieInRadius);
            arc2.outerRadius(pieOutRadius - 30).innerRadius(pieInRadius - 30);
            keyArc.outerRadius(pieOutRadius + 5).innerRadius(pieInRadius);
            // .cornerRadius(3); -- TBD

            // slow down segment selections
            const onmouseover = debounce(key => dispatch.call('mouseover', my, key), 220);
            const onclick = selection => {
                const d = (data || []).find(d => d.key === selection.key);
                dispatch.call('click', my, d);
            };

            const everythingDonut = Array.from(D3rollup(data, v => D3sum(v, g => g.count), g => g.key))
                .map(([key, value]) => ({ key, value }));
            const donut = everythingDonut.slice(0, 42);

            const everythingProg = Array.from(D3rollup(progdata, v => D3sum(v, g => g.count), g => g.key))
                .map(([key, value]) => ({ key, value }));
            const prog = everythingProg.slice(0, 42);

            // uncomment to add 'other' to donut
            // const rest = everything.slice(42);
            // if (rest.length > 0) {
            //     donut.push({ key: 'other', value: rest.reduce((a, c) => a + c.value, 0), rest: true });
            // }

            radius.domain([0, D3max(donut, d => d.value)]);

            const svgj = D3select(this).selectAll('svg').data(['once']);
            const svg = svgj.enter()
                .append('svg')
                .merge(svgj)
                .style('overflow', 'visible')
                .attr('width', size)
                .attr('height', size);

            const gj = svg.selectAll('g.data').data(['once']);
            const g = gj.enter()
                .append('g')
                .attr('class', 'data')
                .merge(gj)
                .attr('transform', `translate(${size / 2},${size / 2})`);

            const gpj = svg.selectAll('g.prog').data(['once']);
            const gp = gpj.enter()
                .append('g')
                .attr('class', 'prog')
                .merge(gpj)
                .attr('transform', `translate(${size / 2},${size / 2})`);

            const tip = D3Tooltips()
                .theme(theme)
                .bounds({ width: size, height: size })
                .arrow(false).offset(0.9);

            // value label
            const dv = group ? group.value : D3sum(everythingDonut, d => d.value);
            // reduce precision for very large numbers DEV-6147
            const format = dv > 1000000 ? '.4s' : ',.0f';
            const v = D3format(format)(dv);
            const text = g.selectAll('text.total')
                .data(['once'])
                .join('text')
                .attr('class', 'total')
                .attr('y', 10)
                .attr('text-anchor', 'middle')
                .style('font-size', '48px')
                .style('pointer-events', 'none')
                .style('fill', theme.palette.text.primary)
                .text((progdata.length ? progdata[0].count : 0) + '%');

            const progressLabel = g.selectAll('text.progress')
                .data(['once'])
                .join('text')
                .attr('class', 'progress')
                .attr('y', 50)
                .attr('text-anchor', 'middle')
                .style('font-size', () => '14px')
                .style('pointer-events', 'none')
                .style('fill', theme.palette.text.primary)
                .text('Complete');

            // tooltips
            svg.selectAll('g.donut-tip').data(['donut-tip']).join('g')
                .attr('class', 'donut-tip')
                .attr('transform', `translate(${0},${margin.top})`)
                .call(tip);

            // used for expanding segment onmouseover
            g.selectAll('path.data')
                .data(pie(donut), function (d) { return d.data.key })
                .join('path')
                .attr('class', 'data')
                .on('mouseover', function (e, d) {
                    if (d.data.rest) return;
                    D3select(this)
                        .transition()
                        .duration(theme.transitions.duration.short)
                        .attr('d', keyArc);
                    onmouseover(d.data);
                    // display, position and text for the tooltip
                    tip.move(-20, 0, d.data.key.split(', ').map(k => k.split(' = ')));
                    text.text(D3format(',.0f')(d.value));
                    progressLabel.text(d.data.key);
                })
                .on('mouseout', function (e, d) {
                    if (d.data.rest) return;
                    D3select(this)
                        .transition()
                        .duration(theme.transitions.duration.short)
                        .attr('d', arc);
                    onmouseover(undefined);
                    tip.hide();
                    text.text((progdata.length ? progdata[0].count : 0) + '%');
                    progressLabel.text('Complete');
                })
                .on('click', function (e, d) {
                    if (d.data.rest) return;
                    tip.hide();
                    onclick(d.data);
                })
                .attr('d', arc)
                .attr('transform', 'rotate(90)')
                .style('cursor', d => d.data.rest ? '' : 'pointer')
                .style('fill', d => colors(d.data.key));

            gp.selectAll('path.prog')
                .data(pie(prog), (d) => (d.data.key))
                .join('path')
                .attr('class', 'prog')
                .attr('d', arc2)
                .attr('transform', 'rotate(90)')
                .style('cursor', d => d.data.rest ? '' : 'pointer')
                .style('fill', d => progColors(d.data.key));
        });
    }

    my.progdata = function (v) {
        if (!arguments.length) return progdata;
        progdata = v;
        return my;
    };

    my.size = function (v) {
        if (!arguments.length) return size;
        size = v;
        return my;
    };

    my.colors = function (v) {
        if (!arguments.length) return colors;
        colors = v;
        return my;
    };

    my.progcolors = function (v) {
        if (!arguments.length) return progColors;
        progColors = v;
        return my;
    };

    my.date = function (v) {
        if (!arguments.length) return date;
        date = v;
        return my;
    };

    my.group = function (v) {
        if (!arguments.length) return group;
        group = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3ProgDonut;
